'use client'

import type { View } from 'product-list/types/View'
import {
	getProductListState,
	type ProductListState,
} from 'product-list/utils/productListState'
import { create } from 'zustand'
import { subscribeWithSelector } from 'zustand/middleware'

export type ProductListStoreApi = ReturnType<typeof createProductListStore>

export type ProductListStoreParams = {
	defaultView: View
	initialView: View
	initialCount: number
	name: string
	total: number
	shouldRenderSlideshowAnimation: boolean
	hasInspirationalPresentation: boolean
}

export interface ProductListStore {
	// State
	view: View
	defaultView: View
	count: number
	initialCount: number
	lastSlotId?: string
	name: string
	total: number
	shouldRenderSlideshowAnimation: boolean
	slideshowAnimationRendered: boolean
	hasInspirationalPresentation: boolean
	storedState: ProductListState | undefined

	// Actions
	setView: (view: View) => void
	setCount: (count: number) => void
	increaseCount: (amount: number) => void
	setSlideshowAnimationRendered: (rendered: boolean) => void
	resetCount: () => void
	restoreStoredState: () => void
}

export const createProductListStore = (initialParams: ProductListStoreParams) =>
	create<ProductListStore>()(
		subscribeWithSelector((set, get) => ({
			view: initialParams.initialView,
			defaultView: initialParams.defaultView,
			count: initialParams.initialCount,
			initialCount: initialParams.initialCount,
			name: initialParams.name,
			total: initialParams.total,
			shouldRenderSlideshowAnimation:
				initialParams.shouldRenderSlideshowAnimation,
			slideshowAnimationRendered: false,
			hasInspirationalPresentation: initialParams.hasInspirationalPresentation,
			storedState: getProductListState(initialParams.name),

			setView: (view) => set({ view }),
			setCount: (count) => set({ count: Math.min(count, get().total) }),
			increaseCount: (amount) => get().setCount(get().count + amount),
			setSlideshowAnimationRendered: (slideshowAnimationRendered) =>
				set({ slideshowAnimationRendered }),
			resetCount: () => set({ count: get().initialCount }),
			restoreStoredState: () => {
				const { storedState } = get()
				if (storedState) {
					set({
						count: storedState.count,
						view: storedState.view,
						lastSlotId: storedState.slotId,
					})
				}
			},
		}))
	)
